import { Link } from 'react-router-dom'
import { P, H, OL, UL, Li, Callout, Code, Screenshot } from '../components'

// ─── Last verified: 16 May 2026 (2 passes) ──────────────────────────────────
// Verified against:
//   - src/pages/Settings.js TeamTab
//     Tab is "Team", not "Practice users".
//     Three roles: Owner, Admin, Assistant.
//     Only one Owner per practice. Invite form's role dropdown is
//     Admin or Assistant (you can't invite an Owner).
//     Role permissions panel at top of tab:
//       Owner: Full access including billing and team management
//       Admin: Full access except billing and team management
//       Assistant: Can view, upload and add notes. Cannot delete anything.
//     Only the Owner sees the invite form, the role-change select on
//     each member, the remove button, and the cancel-pending-invitation
//     button. Admin and Assistant see a notice: "Only the practice
//     owner can invite or manage team members."
//   - supabase/functions/invite-member/index.ts: 7-day expiry on invites
//     (NOT 14 days).
//   - No resend invitation feature; Owner can cancel and re-invite.
//   - No 2FA feature in the codebase.
//   - removeMember just deletes the practice_members row; doesn't sign
//     out the user. The user stays authenticated as a Supabase user but
//     loses all practice access immediately (RLS denies subsequent
//     queries). The article describes this in plain English (their
//     login still exists but does not get them anywhere in the practice)
//     rather than naming Supabase, which is internal jargon.
// Removed:
//   - "Member" role -- actual role is "Assistant"
//   - "Settings -> Practice users" -- actual path is "Settings -> Team"
//   - "Multiple owners" -- only one Owner per practice
//   - "Admin can invite/remove members" -- Owner-only
//   - 14-day invitation expiry -- actually 7 days
//   - "Resend invitation" feature -- doesn't exist
//   - "Two-factor authentication" section -- 2FA isn't implemented
//   - "Active sessions signed out" -- misleading; user keeps their
//     Supabase session but loses practice access on next API call
// Pass 2 added:
//   - Role permissions legend at top of tab
//   - "You can't change your own role" UI behaviour
//   - Owner can't be reassigned via the dropdown (only Admin/Assistant
//     can be picked)

export default {
  title:   'Managing practice users',
  summary: 'Inviting team members, setting roles, removing access, and what each role can do.',
  body: (
    <>
      <P>
        Every Receipt Chaser plan includes unlimited practice users at
        no extra cost. There's no reason to share login details: give
        each person their own account, you'll know who did what, and
        you can revoke access cleanly when someone leaves.
      </P>

      <H>The three roles</H>
      <P>
        Receipt Chaser uses three role levels. The Team tab in your
        Settings shows them in a small panel at the top so you don't
        need to remember which is which:
      </P>
      <UL>
        <Li>
          <strong>Owner.</strong> Full access including billing and
          team management. The person who signed up is the Owner.
          There is exactly one Owner per practice.
        </Li>
        <Li>
          <strong>Admin.</strong> Full access to clients, requests,
          and settings, except billing and team management. Use this
          role for a partner, senior bookkeeper, or office manager.
        </Li>
        <Li>
          <strong>Assistant.</strong> Can view, upload, and add notes.
          Cannot delete anything (requests, clients, transactions, or
          uploaded files). Use this role for juniors or admin staff
          where you want a second pair of hands without giving them
          the power to remove records.
        </Li>
      </UL>

      <Callout variant="tip" title="Pick the lowest role that works">
        It's tempting to give everyone Admin, but Assistant covers
        most day-to-day tasks (working through requests, adding
        notes, uploading on a client's behalf via the client link)
        and avoids accidental deletions. You can upgrade someone
        later if they need more power.
      </Callout>

      <H>Inviting a new user</H>
      <OL>
        <Li>
          Go to <Code>Settings → Team</Code>. The tab is only visible
          to the practice owner.
        </Li>
        <Li>
          At the bottom of the page, find the "Invite a team member"
          form.
        </Li>
        <Li>Enter their email address and pick a role (Admin or Assistant).</Li>
        <Li>
          Click <Code>Send invite</Code>. They receive an email with
          a link to create their account and join your practice.
        </Li>
        <Li>
          The pending invitation appears in the Pending invitations
          section above the form, with the expiry date.
        </Li>
      </OL>

      <Screenshot
        src="/support-screenshots/10 - team-members.jpg"
        alt="The Settings page with the Team tab selected. At the top is a Role permissions reference panel listing Owner, Admin, and Assistant with their permissions. Below: three current members (Owner with no controls, Admin with a role dropdown and delete icon, Assistant with the same), one pending invitation with expiry date and Cancel link, and an Invite a team member form at the bottom with email field, role dropdown, and Send invite button."
        caption="The Team tab in Settings, showing the role permissions reference at the top, current members with their role badges and management controls, pending invitations, and the invite form at the bottom."
      />

      <Callout variant="info" title="Can I invite someone as Owner?">
        No. The role dropdown only offers Admin or Assistant. There
        can be exactly one Owner per practice, and that's the person
        who originally signed up.
      </Callout>

      <H>Pending invitations</H>
      <P>
        Invitations expire after <strong>7 days</strong>. If the
        recipient hasn't accepted by then, the invitation becomes
        invalid and you'll need to send a fresh one. The Team tab
        shows the expiry date next to each pending invite.
      </P>
      <P>
        While an invitation is pending, the Owner sees a{' '}
        <Code>Cancel</Code> button next to it. There is no "resend"
        option. If the recipient lost the email or the invite has
        expired, cancel the original and create a new one with the
        same email address.
      </P>

      <H>Changing someone's role</H>
      <OL>
        <Li>Go to <Code>Settings → Team</Code> (Owner only).</Li>
        <Li>
          Next to the member's name, use the role dropdown to pick
          Admin or Assistant.
        </Li>
        <Li>
          The change takes effect immediately. The person doesn't
          need to sign out and back in.
        </Li>
      </OL>
      <P>
        You can't change your own role from this screen, and you
        can't promote anyone to Owner. Both are intentional
        constraints. If you genuinely need to hand the practice over
        to someone else, see the section below.
      </P>

      <H>Removing a user</H>
      <P>
        When someone leaves the practice, remove them straight away.
      </P>
      <OL>
        <Li>Go to <Code>Settings → Team</Code> (Owner only).</Li>
        <Li>
          Click the 🗑 icon next to their name. Confirm the prompt.
        </Li>
      </OL>
      <P>
        Their next action inside Receipt Chaser fails with a
        permissions error, effectively locking them out. They no
        longer have access to anything in your practice: no clients,
        no requests, no settings, no Team tab. Their login email and
        password are unchanged on their end, but logging in won't
        get them anywhere in your account.
      </P>

      <Callout variant="warning" title="What removal does and doesn't do">
        Removing a user revokes their practice access. It does NOT
        delete the work they did (requests they sent, files they
        uploaded for clients, settings they changed). Those records
        stay in place. The removed user just can no longer do
        anything new.
      </Callout>

      <H>What if I need to hand over the practice to someone else?</H>
      <P>
        There isn't a self-service way to transfer the Owner role.
        If the original Owner is leaving and the practice needs to
        continue, send us a message via the{' '}
        <Link to="/contact">Contact</Link> page describing the
        situation, and we can help. Day-to-day work can continue
        in the meantime: an Admin can keep things running, just not
        access billing or invite new members.
      </P>

      <H>Related guides</H>
      <UL>
        <Li>
          <Link to="/support/setting-up-your-account">Setting up your account</Link>
        </Li>
        <Li>
          <Link to="/support/changing-or-cancelling-your-plan">Changing or cancelling your plan</Link>
        </Li>
      </UL>
    </>
  ),
}
